import { create } from 'zustand';
import { apiPost, apiGet, setToken, clearToken, getToken, setAdminToken, clearAdminToken, setRefreshToken } from '../services/api';

interface AuthUser {
  id: string;
  username: string;
  role: 'admin' | 'user';
  allowed_sites: string[];
}

interface AuthState {
  isAuth: boolean;
  token: string | null;
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  restoreSession: () => Promise<void>;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuth: false,
  token: null,
  user: null,

  login: async (username: string, password: string) => {
    try {
      const data = await apiPost<{
        success: boolean;
        data: { accessToken: string; refreshToken: string; expiresIn: string; user: AuthUser };
      }>('/api/auth/login', { username, password });
      if (data.success && data.data?.accessToken) {
        setToken(data.data.accessToken);
        if (data.data.refreshToken) {
          setRefreshToken(data.data.refreshToken);
        }
        if (data.data.user?.role === 'admin') {
          setAdminToken(data.data.accessToken);
        }
        set({ isAuth: true, token: data.data.accessToken, user: data.data.user });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  logout: () => {
    clearToken();
    clearAdminToken();
    set({ isAuth: false, token: null, user: null });
  },

  restoreSession: async () => {
    const token = getToken();
    if (!token) return;
    try {
      const data = await apiGet<{
        success: boolean;
        data?: { user?: AuthUser };
      }>('/api/auth/me');
      if (data.success && data.data?.user) {
        set({ isAuth: true, token, user: data.data.user });
        if (data.data.user.role === 'admin') {
          setAdminToken(token);
        }
      } else {
        clearToken();
      }
    } catch {
      clearToken();
      clearAdminToken();
    }
  },

  isAdmin: () => {
    const { user } = get();
    return user?.role === 'admin';
  },
}));
